import React, { useEffect } from 'react';

import { HStack, Stack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useParams, useRouter } from 'next/navigation';


import { OutlineButton, PrimaryButton } from '@components/custom-buttons';
import { FormControlSelectField, FormControlTextArea } from '@components/custom-form-control';
import { useAppDispatch, useAppSelector } from '@hooks/store';
import { useLazyGetMeteePlansQuery } from '@modules/billings/api';
import { MenteePlans } from '@modules/billings/types';
import { SessionPayload } from '@modules/bookings/types';
import { ServicesSchema } from '@modules/find-mentor/schema/ServicesSchema';
import {
    clearBookingData,
    setBookingData,
    setRedirectPath,
} from '@modules/find-mentor/slice/bookingSlice';
import { SlotToBook } from '@modules/find-mentor/types';
import { paths } from '@routes/path';
import { showToast } from '@slices/apiSlice';

type ServicesFormProps = {
    mentorId: string;
    onSubmit: (values: SessionPayload) => void;
    isLoading: boolean;
    onCancel: () => void;
    services: {
        value: string;
        key: string;
    }[];
    selectedSlot: SlotToBook | undefined;
    onOpenDialog: () => void;
    isNotAuthenticated: boolean;
    mentorName: string;
    menteeName: string;
    plansData: MenteePlans | undefined;
};

const ServicesForm = ({
    onSubmit,
    isLoading,
    onCancel,
    services,
    selectedSlot,
    mentorId,
    mentorName,
    menteeName,
    onOpenDialog,
    isNotAuthenticated,
    plansData,
}: ServicesFormProps) => {
    const initialValues = {
        title: `${mentorName} : ${menteeName}`,
        serviceId: '',
        notes: '',
    };

    const dispatch = useAppDispatch();
    const router = useRouter();
    const params = useParams();
    const formData = useAppSelector(state => state.reducer.booking.formData);
    const shouldBook = useAppSelector(state => state.reducer.booking.shouldBook);
    const redirectPath = useAppSelector(state => state.reducer.booking.redirectPath);

    const [fetchPlans] = useLazyGetMeteePlansQuery();

    const handleSubmit = async (values: { notes: string; serviceId: string; title: string }) => {
        // if (isNotAuthenticated) {
        //     onOpenDialog();
        //     return;
        // }

        dispatch(clearBookingData());

        const payload: SessionPayload = {
            title: values.title,
            notes: values.notes,
            serviceId: values.serviceId,
            // meetingUrl: 'https://meeting-url.com',
            startDate: selectedSlot?.startTime!,
            endDate: selectedSlot?.endEtime!,
            mentorId,
        };

        const slug = params?.slug;
        dispatch(setBookingData(payload));
        dispatch(setRedirectPath(`/find-mentor/${slug}`));

        if (isNotAuthenticated) {
            router.push(`/mentee/login?redirectTo=/find-mentor/${params?.slug}`);
            return;
        }

        const plans = plansData?.menteePlans || [];
        const hasActivePlan = plans.some(plan => plan.totalCredits > plan.usedCredits);

        if (!hasActivePlan) {
            router.push(`/pricing?redirectTo=/find-mentor/${params?.slug}`);
            return;
        }

        onSubmit(payload);
        dispatch(clearBookingData());
    };

    useEffect(() => {
        if (!formData || !shouldBook) return;
        const slug = params?.slug;

        if (isNotAuthenticated) {
            dispatch(setRedirectPath(`/find-mentor/${slug}`));
            router.push(`/mentee/login?redirectTo=/find-mentor/${slug}`);
            return;
        }

        fetchPlans({
            offset: 0,
            limit: 1000,
        })
            .unwrap()
            .then(latest => {
                const plans = latest.menteePlans || [];
                const hasActivePlan = plans.some(plan => plan.totalCredits > plan.usedCredits);

                if (!hasActivePlan) {
                    dispatch(setRedirectPath(`/find-mentor/${slug}`));
                    router.push(`/pricing?redirectTo=/find-mentor/${slug}`);
                    return;
                }

                onSubmit(formData);
                dispatch(clearBookingData());
            })
            .catch(() => {
                showToast({
                    variant: 'error',
                    description: 'Could not verify your plan. Please try again.',
                });
                router.push(redirectPath ?? paths.general.findMentor);
                dispatch(clearBookingData());
            });
    }, [formData, shouldBook, isNotAuthenticated, dispatch, params?.slug, onSubmit, router]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={ServicesSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ values }) => (
                <Form>
                    <Stack width="100%" spacing={4}>
                        <FormControlSelectField
                            name="serviceId"
                            placeholder="Select Service"
                            label="Select Service"
                            options={services}
                            isRequired
                        />
                        <FormControlTextArea
                            name="notes"
                            placeholder="Type your notes here..."
                            label="Notes"
                        />{' '}
                    </Stack>
                    <HStack align="flex-start" justify="space-between" gap={4} mt={8}>
                        <OutlineButton
                            borderColor="border"
                            color="mainText"
                            buttonLabel="Back"
                            type="button"
                            onClick={onCancel}
                        />
                        <PrimaryButton
                            buttonLabel="Confirm"
                            type="submit"
                            isLoading={isLoading}
                            isDisabled={isLoading}
                        />
                    </HStack>
                </Form>
            )}
        </Formik>
    );
};

export default ServicesForm;
